import { useState, useEffect, useCallback } from 'react'
import { 
  getAtendimentos, createAtendimento, deleteAtendimento,
  getLocaisTrabalho, createLocalTrabalho, deleteLocalTrabalho,
  getMedicos, createMedico, deleteMedico,
  getProcedimentos, createProcedimento, deleteProcedimento
} from '@/services/api'
import { formatDate } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, ClipboardList, Trash2, Loader2, Building, User, Activity, Calendar } from 'lucide-react'
import { toast } from 'sonner'

export default function AtendimentosPage() {
  const [atendimentos, setAtendimentos] = useState([])
  const [locais, setLocais] = useState([])
  const [medicos, setMedicos] = useState([])
  const [procedimentos, setProcedimentos] = useState([])
  const [loading, setLoading] = useState(true)
  
  const [dialogAtendimento, setDialogAtendimento] = useState(false)
  const [dialogLocal, setDialogLocal] = useState(false)
  const [dialogMedico, setDialogMedico] = useState(false)
  const [dialogProcedimento, setDialogProcedimento] = useState(false)

  const [atendimentoForm, setAtendimentoForm] = useState({
    nome_paciente: '',
    cartao_sus: '',
    tipo_atendimento: 'Consulta',
    medico_id: '',
    local_trabalho_id: '',
    procedimento_id: '',
    dia_atendimento: ''
  })

  const [localForm, setLocalForm] = useState({ nome: '', endereco: '', responsavel: '' })
  const [medicoForm, setMedicoForm] = useState({ nome: '', especialidade: '', local_trabalho_id: '' })
  const [procedimentoForm, setProcedimentoForm] = useState({ nome: '', tipo: '' })

  const fetchAllData = useCallback(async () => {
    try {
      const [atendRes, locaisRes, medicosRes, procRes] = await Promise.all([
        getAtendimentos(),
        getLocaisTrabalho(),
        getMedicos(),
        getProcedimentos()
      ])
      setAtendimentos(atendRes.data)
      setLocais(locaisRes.data)
      setMedicos(medicosRes.data)
      setProcedimentos(procRes.data)
    } catch (error) {
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  // Atendimento handlers
  const handleCreateAtendimento = async (e) => {
    e.preventDefault()
    try {
      await createAtendimento(atendimentoForm)
      toast.success('Atendimento registrado com sucesso!')
      setDialogAtendimento(false)
      setAtendimentoForm({
        nome_paciente: '',
        cartao_sus: '',
        tipo_atendimento: 'Consulta',
        medico_id: '',
        local_trabalho_id: '',
        procedimento_id: '',
        dia_atendimento: ''
      })
      fetchAllData()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar atendimento')
    }
  }

  const handleDeleteAtendimento = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este atendimento?')) return
    try {
      await deleteAtendimento(id)
      toast.success('Atendimento excluído com sucesso!')
      fetchAllData()
    } catch (error) {
      toast.error('Erro ao excluir atendimento')
    }
  }

  // Local handlers
  const handleCreateLocal = async (e) => {
    e.preventDefault()
    try {
      await createLocalTrabalho(localForm)
      toast.success('Local cadastrado com sucesso!')
      setDialogLocal(false)
      setLocalForm({ nome: '', endereco: '', responsavel: '' })
      fetchAllData()
    } catch (error) {
      toast.error('Erro ao cadastrar local')
    }
  }

  const handleDeleteLocal = async (id) => {
    if (!window.confirm('Excluir este local?')) return
    try {
      await deleteLocalTrabalho(id)
      toast.success('Local excluído!')
      fetchAllData()
    } catch (error) {
      toast.error('Erro ao excluir local')
    }
  }

  // Medico handlers
  const handleCreateMedico = async (e) => {
    e.preventDefault()
    try {
      await createMedico(medicoForm)
      toast.success('Médico cadastrado com sucesso!')
      setDialogMedico(false)
      setMedicoForm({ nome: '', especialidade: '', local_trabalho_id: '' })
      fetchAllData()
    } catch (error) {
      toast.error('Erro ao cadastrar médico')
    }
  }

  const handleDeleteMedico = async (id) => {
    if (!window.confirm('Excluir este médico?')) return
    try {
      await deleteMedico(id)
      toast.success('Médico excluído!')
      fetchAllData()
    } catch (error) {
      toast.error('Erro ao excluir médico')
    }
  }

  // Procedimento handlers
  const handleCreateProcedimento = async (e) => {
    e.preventDefault()
    try {
      await createProcedimento(procedimentoForm)
      toast.success('Procedimento cadastrado com sucesso!')
      setDialogProcedimento(false)
      setProcedimentoForm({ nome: '', tipo: '' })
      fetchAllData()
    } catch (error) {
      toast.error('Erro ao cadastrar procedimento')
    }
  }

  const handleDeleteProcedimento = async (id) => {
    if (!window.confirm('Excluir este procedimento?')) return
    try {
      await deleteProcedimento(id)
      toast.success('Procedimento excluído!')
      fetchAllData()
    } catch (error) {
      toast.error('Erro ao excluir procedimento')
    }
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Atendimentos</h1>
          <p className="text-slate-500 mt-1">Registre atendimentos e exames de pacientes</p>
        </div>
        <Dialog open={dialogAtendimento} onOpenChange={setDialogAtendimento}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="novo-atendimento-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Atendimento
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Novo Atendimento</DialogTitle>
              <DialogDescription>Registre um novo atendimento ou exame</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAtendimento} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Paciente</Label>
                  <Input
                    value={atendimentoForm.nome_paciente}
                    onChange={(e) => setAtendimentoForm({ ...atendimentoForm, nome_paciente: e.target.value })}
                    required
                    data-testid="input-paciente"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cartão SUS</Label>
                  <Input
                    value={atendimentoForm.cartao_sus}
                    onChange={(e) => setAtendimentoForm({ ...atendimentoForm, cartao_sus: e.target.value })}
                    required
                    data-testid="input-sus"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Atendimento</Label>
                  <Select value={atendimentoForm.tipo_atendimento} onValueChange={(v) => setAtendimentoForm({ ...atendimentoForm, tipo_atendimento: v })}>
                    <SelectTrigger data-testid="select-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Consulta">Consulta</SelectItem>
                      <SelectItem value="Exame">Exame</SelectItem>
                      <SelectItem value="Retorno">Retorno</SelectItem>
                      <SelectItem value="Procedimento">Procedimento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data do Atendimento</Label>
                  <Input
                    type="date"
                    value={atendimentoForm.dia_atendimento}
                    onChange={(e) => setAtendimentoForm({ ...atendimentoForm, dia_atendimento: e.target.value })}
                    required
                    data-testid="input-data"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Médico</Label>
                <Select value={atendimentoForm.medico_id} onValueChange={(v) => setAtendimentoForm({ ...atendimentoForm, medico_id: v })}>
                  <SelectTrigger data-testid="select-medico">
                    <SelectValue placeholder="Selecione o médico" />
                  </SelectTrigger>
                  <SelectContent>
                    {medicos.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.nome} - {m.especialidade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Local de Trabalho</Label>
                <Select value={atendimentoForm.local_trabalho_id} onValueChange={(v) => setAtendimentoForm({ ...atendimentoForm, local_trabalho_id: v })}>
                  <SelectTrigger data-testid="select-local">
                    <SelectValue placeholder="Selecione o local" />
                  </SelectTrigger>
                  <SelectContent>
                    {locais.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Procedimento (opcional)</Label>
                <Select value={atendimentoForm.procedimento_id || "none"} onValueChange={(v) => setAtendimentoForm({ ...atendimentoForm, procedimento_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="select-procedimento">
                    <SelectValue placeholder="Selecione o procedimento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {procedimentos.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="salvar-atendimento-btn">
                  Registrar Atendimento
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Atendimentos</p>
                <p className="text-2xl font-semibold text-slate-900">{atendimentos.length}</p>
              </div>
              <ClipboardList className="w-8 h-8 text-teal-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Locais</p>
                <p className="text-2xl font-semibold text-blue-600">{locais.length}</p>
              </div>
              <Building className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Médicos</p>
                <p className="text-2xl font-semibold text-emerald-600">{medicos.length}</p>
              </div>
              <User className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Procedimentos</p>
                <p className="text-2xl font-semibold text-purple-600">{procedimentos.length}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="atendimentos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="atendimentos" data-testid="tab-atendimentos">Atendimentos</TabsTrigger>
          <TabsTrigger value="locais" data-testid="tab-locais">Locais</TabsTrigger>
          <TabsTrigger value="medicos" data-testid="tab-medicos">Médicos</TabsTrigger>
          <TabsTrigger value="procedimentos" data-testid="tab-procedimentos">Procedimentos</TabsTrigger>
        </TabsList>

        {/* Atendimentos Tab */}
        <TabsContent value="atendimentos">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : atendimentos.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <ClipboardList className="w-12 h-12 mb-4 text-slate-300" />
                  <p>Nenhum atendimento registrado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Paciente</TableHead>
                      <TableHead>Cartão SUS</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Médico</TableHead>
                      <TableHead>Local</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {atendimentos.map((atend) => (
                      <TableRow key={atend.id} className="hover:bg-slate-50" data-testid={`atend-row-${atend.id}`}>
                        <TableCell className="font-medium">{atend.nome_paciente}</TableCell>
                        <TableCell>{atend.cartao_sus}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                            {atend.tipo_atendimento}
                          </Badge>
                        </TableCell>
                        <TableCell>{atend.medico_nome || '-'}</TableCell>
                        <TableCell>{atend.local_trabalho_nome || '-'}</TableCell>
                        <TableCell>{formatDate(atend.dia_atendimento)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => handleDeleteAtendimento(atend.id)} data-testid={`btn-delete-${atend.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locais Tab */}
        <TabsContent value="locais">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Locais de Trabalho</CardTitle>
              <Dialog open={dialogLocal} onOpenChange={setDialogLocal}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="novo-local-btn">
                    <Plus className="w-4 h-4 mr-1" /> Novo Local
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Novo Local de Trabalho</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateLocal} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nome</Label>
                      <Input value={localForm.nome} onChange={(e) => setLocalForm({ ...localForm, nome: e.target.value })} required data-testid="input-local-nome" />
                    </div>
                    <div className="space-y-2">
                      <Label>Endereço</Label>
                      <Input value={localForm.endereco} onChange={(e) => setLocalForm({ ...localForm, endereco: e.target.value })} required data-testid="input-local-endereco" />
                    </div>
                    <div className="space-y-2">
                      <Label>Responsável</Label>
                      <Input value={localForm.responsavel} onChange={(e) => setLocalForm({ ...localForm, responsavel: e.target.value })} required data-testid="input-local-responsavel" />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="salvar-local-btn">Cadastrar</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Nome</TableHead>
                    <TableHead>Endereço</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {locais.map((local) => (
                    <TableRow key={local.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{local.nome}</TableCell>
                      <TableCell>{local.endereco}</TableCell>
                      <TableCell>{local.responsavel}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => handleDeleteLocal(local.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Médicos Tab */}
        <TabsContent value="medicos">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Médicos</CardTitle>
              <Dialog open={dialogMedico} onOpenChange={setDialogMedico}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="novo-medico-btn">
                    <Plus className="w-4 h-4 mr-1" /> Novo Médico
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Novo Médico</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateMedico} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nome</Label>
                      <Input value={medicoForm.nome} onChange={(e) => setMedicoForm({ ...medicoForm, nome: e.target.value })} required data-testid="input-medico-nome" />
                    </div>
                    <div className="space-y-2">
                      <Label>Especialidade</Label>
                      <Input value={medicoForm.especialidade} onChange={(e) => setMedicoForm({ ...medicoForm, especialidade: e.target.value })} required data-testid="input-medico-especialidade" />
                    </div>
                    <div className="space-y-2">
                      <Label>Local de Trabalho</Label>
                      <Select value={medicoForm.local_trabalho_id} onValueChange={(v) => setMedicoForm({ ...medicoForm, local_trabalho_id: v })}>
                        <SelectTrigger data-testid="select-medico-local">
                          <SelectValue placeholder="Selecione o local" />
                        </SelectTrigger>
                        <SelectContent>
                          {locais.map((l) => (
                            <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="salvar-medico-btn">Cadastrar</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Nome</TableHead>
                    <TableHead>Especialidade</TableHead>
                    <TableHead>Local</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {medicos.map((medico) => (
                    <TableRow key={medico.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{medico.nome}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{medico.especialidade}</Badge>
                      </TableCell>
                      <TableCell>{medico.local_trabalho_nome || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => handleDeleteMedico(medico.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Procedimentos Tab */}
        <TabsContent value="procedimentos">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Procedimentos</CardTitle>
              <Dialog open={dialogProcedimento} onOpenChange={setDialogProcedimento}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="novo-procedimento-btn">
                    <Plus className="w-4 h-4 mr-1" /> Novo Procedimento
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Novo Procedimento</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateProcedimento} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nome</Label>
                      <Input value={procedimentoForm.nome} onChange={(e) => setProcedimentoForm({ ...procedimentoForm, nome: e.target.value })} required data-testid="input-proc-nome" />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <Input value={procedimentoForm.tipo} onChange={(e) => setProcedimentoForm({ ...procedimentoForm, tipo: e.target.value })} required placeholder="Ex: Exame, Cirurgia" data-testid="input-proc-tipo" />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="salvar-proc-btn">Cadastrar</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {procedimentos.map((proc) => (
                    <TableRow key={proc.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{proc.nome}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{proc.tipo}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => handleDeleteProcedimento(proc.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
